"use client";

import type { ReactNode } from "react";
import { type Provenance, ProvenancePill } from "./ProvenancePill";

/* -------------------------------------------------------------------------- */
/* OrgDataRow                                                                  */
/* -------------------------------------------------------------------------- */

export interface OrgDataRowProps {
  /**
   * Left-side label.  Accepts ReactNode so callers can embed `ImpactChip`
   * alongside the text.
   */
  label: ReactNode;
  /**
   * Right-side value slot.  Typically an `EditableValue` or static text.
   */
  children: ReactNode;
  /**
   * When provided, appends a `ProvenancePill` after the value slot.
   */
  provenance?: Provenance;
  /**
   * Secondary text rendered below the label (used by BaselineRow).
   */
  description?: string;
  /** Extra class names forwarded to the outer `<li>`. */
  className?: string;
}

/**
 * Generic `<li>` layout for org-profile data rows.
 *
 * ```
 * ┌─────────────────────────────────────────────────────┐
 * │ [label]  [description?]        [value]  [provenance] │
 * └─────────────────────────────────────────────────────┘
 * ```
 *
 * When `description` is provided, the label and description stack vertically
 * on the left, taking up more vertical space.
 */
export function OrgDataRow({
  label,
  children,
  provenance,
  description,
  className,
}: OrgDataRowProps) {
  return (
    <li
      className={[
        "flex items-center gap-3",
        "min-h-[40px] py-2",
        "border-b border-(--border-subtle)",
        "last:border-b-0",
        className ?? "",
      ].join(" ")}
    >
      {/* Label column */}
      {description ? (
        <div className="flex flex-col gap-0.5 shrink-0">
          <span className="text-sm text-(--fg-primary) leading-snug flex items-center gap-1.5">
            {label}
          </span>
          <span className="text-xs text-(--fg-muted) leading-snug">
            {description}
          </span>
        </div>
      ) : (
        <span className="text-sm text-(--fg-primary) shrink-0 flex items-center gap-1.5">
          {label}
        </span>
      )}

      {/* Value + provenance — pushed to the right */}
      <span className="ml-auto flex items-center gap-2 min-w-0">
        <span className="flex items-center text-sm text-(--fg-primary) tabular-nums">
          {children}
        </span>
        {provenance !== undefined && <ProvenancePill kind={provenance} />}
      </span>
    </li>
  );
}
